import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  ButtonSelect,
  Icon,
  InlineMessage,
  InputField,
  Menu,
  Tooltip,
  Typography,
} from '@saviynt/design-system';
import classname from 'classnames';
import PropTypes from 'prop-types';

import { useInvertedLocalize } from '../../utils/localization';

import invertedMessages from './messages';

// See comment at function `handleChange`
// import { getStringDifference } from './utils';
import './CertsModalCommentBox.css';

function CustomMenuItem({ option, onClick }) {
  const textRef = useRef(null);
  const [showTooltip, setShowTooltip] = useState(false);

  useEffect(() => {
    const checkEllipsis = () => {
      if (textRef.current) {
        const element = textRef.current;
        const hasEllipsis =
          element.scrollHeight > element.clientHeight ||
          element.scrollWidth > element.clientWidth;

        setShowTooltip(hasEllipsis);
      }
    };

    checkEllipsis();
    window.addEventListener('resize', checkEllipsis);

    return () => window.removeEventListener('resize', checkEllipsis);
  }, [option.text]);

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onClick();
    }
  };

  const TypographyComponent = (
    <Typography
      ref={textRef}
      kind='body2'
      color='--color-foreground-secondary-bold'
      className='CertsModalCommentBox-menuItem-label'
      lineLimit={2}>
      {option.text}
    </Typography>
  );

  return (
    <button
      type='button'
      className={`CertsModalCommentBox-menuItem ${
        option.isChecked ? 'CertsModalCommentBox-menuItem--checked' : ''
      }`}
      onClick={onClick}
      onKeyDown={handleKeyDown}>
      {showTooltip ? (
        <Tooltip
          maxWidth='22rem'
          trigger={TypographyComponent}
          isWordBreak
          text={option.text}
          placement='top'
        />
      ) : (
        TypographyComponent
      )}
    </button>
  );
}

CustomMenuItem.propTypes = {
  option: PropTypes.shape({
    text: PropTypes.string,
    isChecked: PropTypes.bool,
  }).isRequired,
  onClick: PropTypes.func.isRequired,
};

/*
 * Name: CertsModalCommentBox
 * Accepts:
 *    defaultComments: [] (Array of pre defined comment)
 *    showAlert: boolean (Show the critical messsage when set to true, only when input box is empty)
 *    onChange: fn (function which receives the text from the input box)
 *    The “Other” label is not translated because the check is mandatory to display the “Other” option to the user.
 */
function CertsModalCommentBox({
  defaultComments,
  allowEditPredefinedComments,
  showAlert,
  onChange,
  isDisabled,
  clearModal,
  shouldDisallowBrackets,
  className,
  dataTestId,
  isRequired = true,
  label,
  size,
}) {
  const [predefComments, setPredefComments] = useState([]);
  const [commentDescription, setCommentDescription] = useState('');
  const [isCommentApplied, setIsCommentApplied] = useState(false);

  const [isMenuExpanded, setMenuExpanded] = useState(false);
  const CertsModalCommentBoxClasses = classname(
    'CertsModalCommentBox-wrapper',
    className
  );

  const l = useInvertedLocalize(invertedMessages);

  useEffect(() => {
    setCommentDescription('');
  }, [clearModal]);

  useEffect(() => {
    if (Array.isArray(defaultComments) && defaultComments?.length > 0) {
      setPredefComments(
        [...defaultComments]?.map((comment, index) => ({
          text: comment,
          isChecked: false,
          label: comment,
          value: comment,
          index: index + 1,
        }))
      );
    } else {
      setPredefComments([]);
    }
  }, [defaultComments]);

  const isChipAvailable = Boolean(predefComments.length > 0);

  useEffect(() => {
    onChange(commentDescription);
  }, [commentDescription]);

  const handleCommentPillClick = (id) => {
    const tempPreDefComments = predefComments.map((comment) => {
      if (comment.index === id) {
        // in dropdown make sure once selected, you can unselect on clicking same item
        if (comment?.isChecked) {
          return comment;
        }

        return {
          ...comment,
          isChecked: !comment.isChecked,
        };
      }

      return {
        ...comment,
        isChecked: false,
      };
    });

    const isActiveComment = tempPreDefComments.some(
      (comment) => comment.isChecked && comment.value?.trim() !== l('Other')
    );

    const tempCommentDescription = tempPreDefComments.find(
      (item) => item.index === id && item.value?.trim() !== l('Other')
    )?.text;

    setPredefComments(tempPreDefComments);
    setIsCommentApplied(isActiveComment);
    setCommentDescription(isActiveComment ? tempCommentDescription : '');
    // if (setPredefCommentsRequired) setPredefCommentsRequired(false);
  };

  const handleChange = (text) => {
    // Use `newdescription` to set the comment description if
    // - predefined comment is present and,
    // - on adding text manually,
    // - the comment should reset/blank
    // const newdescription = isCommentApplied
    //   ? getStringDifference(commentDescription, text)
    //   : text;

    setCommentDescription(text);

    // when no predefined comments available enable edit text
    if (!predefComments.some((c) => c.isChecked) || !isChipAvailable) {
      setPredefComments(
        isCommentApplied
          ? predefComments.map((comment) => ({
              ...comment,
              isChecked: false,
            }))
          : predefComments
      );
      setIsCommentApplied(false);
    }
  };

  const isCommentEmpty = commentDescription.trim().length === 0;
  const isCommentTooShort =
    commentDescription.trim().length > 0 &&
    commentDescription.trim().length < 3;

  const isCriticalEnabled = Boolean(
    showAlert && (isCommentEmpty || isCommentTooShort)
  );

  const getCriticalText = () => {
    if (isCommentEmpty) {
      return <InlineMessage kind='missingRequiredField' size='small' />;
    }

    if (isCommentTooShort) {
      return (
        <InlineMessage
          text={l('Your comments must contain more than 2 characters.')}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' color='critical-700' />}
        />
      );
    }

    return null;
  };

  const getCustomMenuItems = (options) => (
    <>
      {options.map((option) => (
        <CustomMenuItem
          key={option.value}
          option={option}
          onClick={() => {
            handleCommentPillClick(option.index);
            setMenuExpanded(!isMenuExpanded);
          }}
        />
      ))}
    </>
  );

  const getPlaceholder = () => {
    if (predefComments?.length === 0) {
      return l('Provide a reason for your decision');
    }

    if (predefComments.some((c) => c.value?.trim() === l('Other'))) {
      return l(
        'Select a predefined comment or choose "Other" to enter your own.'
      );
    }

    return l('Select a pre-defined comment');
  };

  return (
    <Box
      tag='div'
      className={CertsModalCommentBoxClasses}
      dataTestId={dataTestId}>
      {isChipAvailable && (
        <Box tag='div' className='CertsModalCommentBox-comments'>
          <Menu
            options={predefComments}
            className='CertsModalCommentBox--customMenu'
            onChange={handleCommentPillClick}
            trigger={
              <ButtonSelect
                kind='rectangle'
                label={l('Quick comments')}
                size={size}
                prefixIcon={
                  <Icon
                    kind='chevronDown'
                    size='smallMedium'
                    rotate={isMenuExpanded ? 180 : 0}
                  />
                }
                className='CertsModalCommentBox--triggerButton'
                isVisualCueEnabled={false}
              />
            }
            isMenuExpanded={isMenuExpanded}
            setIsExpanded={setMenuExpanded}
            CustomMenu={getCustomMenuItems(predefComments)}
          />
        </Box>
      )}
      <Box
        tag='div'
        className={`CertsModalCommentBox-inputBox ${
          isChipAvailable ? '' : 'noCommentsPresent'
        }`}>
        <InputField
          name='basic'
          placeholder={getPlaceholder()}
          label={
            <Typography
              kind='h4'
              mobile='h4-d'
              htmlFor='Required'
              className='CertsModalCommentBox-inputBox-label'>
              {label}
            </Typography>
          }
          value={commentDescription}
          setValue={handleChange}
          kind='multiline'
          isRequired={isRequired}
          isDisabled={
            isDisabled ||
            (predefComments.length > 0 &&
              !predefComments.some((c) => c.isChecked)) ||
            (predefComments.length > 0 &&
              isCommentApplied &&
              !allowEditPredefinedComments &&
              predefComments.find((c) => c.isChecked)?.value?.trim() !==
                l('Other'))
          }
          minRequiredChars={3}
          shouldDisallowBrackets={shouldDisallowBrackets}
          isCritical={isCriticalEnabled}
          CriticalHelperText={getCriticalText()}
        />
      </Box>
    </Box>
  );
}

CertsModalCommentBox.propTypes = {
  defaultComments: PropTypes.arrayOf(PropTypes.string),
  allowEditPredefinedComments: PropTypes.bool,
  showAlert: PropTypes.bool.isRequired,
  onChange: PropTypes.func,
  isDisabled: PropTypes.bool,
  shouldDisallowBrackets: PropTypes.bool,
  clearModal: PropTypes.bool,
  className: PropTypes.string,
  dataTestId: PropTypes.string,
  isRequired: PropTypes.bool,
  label: PropTypes.string,
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

CertsModalCommentBox.defaultProps = {
  defaultComments: [],
  allowEditPredefinedComments: false,
  onChange: () => {},
  isDisabled: false,
  clearModal: true,
  shouldDisallowBrackets: true,
  className: null,
  dataTestId: null,
  isRequired: true,
  label: 'Comments',
  size: 'large',
};

export default CertsModalCommentBox;
